import { formatParisDateTime, type UserListItem } from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Send, Trash2, UserPlus } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/confirm-dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { useCurrentUser } from '@/features/auth/use-auth.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { deleteUser, fetchUsersList, resendInvitation, usersQueryKeys } from './api.js';
import { InviteUserDialog } from './invite-user-dialog.js';

const SORTABLE_COLUMNS = ['email', 'name', 'createdAt', 'lastLoginAt'] as const;

/** Statut synthétique affiché en badge — pas de registre `@flux/shared` pour ces comptes (`README-shell.md`). */
function accountStatusBadge(user: UserListItem): JSX.Element {
  if (user.lockedUntil && new Date(user.lockedUntil).getTime() > Date.now()) {
    return (
      <Badge tone="red">Verrouillé jusqu’à {formatParisDateTime(new Date(user.lockedUntil))}</Badge>
    );
  }
  if (user.activatedAt === null) {
    return <Badge tone="blue">Invitation en attente</Badge>;
  }
  return <Badge tone="green">Actif</Badge>;
}

const helper = createDataTableColumnHelper<UserListItem>();

const columns = helper.columns([
  helper.accessor('email', {
    header: 'Email',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  helper.accessor('name', { header: 'Nom' }),
  helper.display({
    id: 'status',
    header: 'Statut',
    cell: (info) => accountStatusBadge(info.row.original),
  }),
  helper.accessor('createdAt', {
    header: 'Créé le',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.accessor('lastLoginAt', {
    header: 'Dernière connexion',
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <span className="whitespace-nowrap tabular-nums">
          {formatParisDateTime(new Date(value))}
        </span>
      ) : (
        <span className="text-muted-foreground">Jamais connecté</span>
      );
    },
  }),
]);

function buildActionsColumn(
  onResend: (user: UserListItem) => void,
  onDelete: (user: UserListItem) => void,
) {
  return helper.display({
    id: 'actions',
    header: '',
    cell: (info) => {
      const user = info.row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Actions pour ${user.email}`}
            >
              <MoreHorizontal aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {user.activatedAt === null ? (
              <DropdownMenuItem
                onSelect={() => {
                  onResend(user);
                }}
              >
                <Send aria-hidden="true" />
                Renvoyer l’invitation
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              className="text-status-red-fg focus:text-status-red-fg [&_svg]:text-status-red-fg"
              onSelect={() => {
                onDelete(user);
              }}
            >
              <Trash2 aria-hidden="true" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  });
}

/** Écran Utilisateurs — `02` §13. */
export function UtilisateursPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { page, sortBy, sortDir, setPage, setSort } = useDataTableUrlState();
  const { data: currentUser } = useCurrentUser();

  const [inviteOpen, setInviteOpen] = useState(false);
  const deleteDialog = useConfirmDialog<UserListItem>();

  const listParams = {
    page,
    pageSize: 50,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
  };

  const query = useQuery({
    queryKey: usersQueryKeys.list(listParams),
    queryFn: () => fetchUsersList(listParams),
  });

  function invalidateAll(): void {
    void queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
  }

  const resendMutation = useMutation({
    mutationFn: (user: UserListItem) => resendInvitation(user.id),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Invitation renvoyée.');
      invalidateAll();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (user: UserListItem) => deleteUser(user.id),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Compte supprimé.');
      invalidateAll();
      deleteDialog.close();
    },
    onError: (error: unknown) => {
      // `02` §13 : le 409 « dernier compte » a un message déjà en français
      // courant, à afficher tel quel — jamais reformulé ni de garde côté
      // client (voir `apps/server/.../users/service.ts::deleteUser`).
      toast.fromError(error);
    },
  });

  const actionsColumn = buildActionsColumn(
    (user) => {
      resendMutation.mutate(user);
    },
    (user) => {
      deleteDialog.open(user);
    },
  );

  return (
    <Page>
      <PageHeader
        title="Utilisateurs"
        description="Comptes ayant accès à l’interface."
        actions={
          <Button
            type="button"
            onClick={() => {
              setInviteOpen(true);
            }}
          >
            <UserPlus aria-hidden="true" />
            Inviter
          </Button>
        }
      />

      <DataTable
        columns={[...columns, actionsColumn]}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={50}
        sortBy={sortBy}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={query.isPending}
        isError={query.isError}
        {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        emptyState={{
          title: 'Aucun utilisateur',
          description: 'Invitez un premier utilisateur pour lui donner accès à l’interface.',
          action: (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setInviteOpen(true);
              }}
            >
              <UserPlus aria-hidden="true" />
              Inviter
            </Button>
          ),
        }}
      />

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteDialog.close();
          }
        }}
        title="Supprimer ce compte"
        description={
          deleteDialog.target
            ? `Le compte « ${deleteDialog.target.email} »${deleteDialog.target.id === currentUser?.id ? ' (votre compte)' : ''} sera définitivement supprimé et toutes ses sessions seront invalidées.`
            : ''
        }
        confirmLabel="Supprimer"
        isConfirming={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteDialog.target) {
            deleteMutation.mutate(deleteDialog.target);
          }
        }}
      />
    </Page>
  );
}
